import { Box } from "@mui/material";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useTransactions } from "api/transactions";
import { FormButtons } from "components/FormControls/FormButtons";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate, useParams } from "react-router-dom";
import { TransactionBackendModel, TransactionItem } from "types/transactions";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import * as Yup from "yup";
import { MemberDetails } from "./MemberDetails";
import { useStyles } from "./Transaction.styles";
import { TransactionDetails } from "./TransactionDetails";
import { TransactionProducts } from "./TransactionProducts/TransactionProducts";
import { initialValues } from "./helpers";

export function TransactionForm(props: {
  editMode?: boolean;
  initialValues?: TransactionBackendModel;
}) {
  const classes = useStyles();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const { message } = useMessage();
  const { addTransaction, editTransaction } = useTransactions();
  const { id: transactionID } = useParams<{ id: string }>();

  const initialFormData =
    props.editMode && props.initialValues
      ? {
          ...props.initialValues,
          channelName: props.initialValues.channelName,
          header: {
            documentNumber: props.initialValues.header.documentNumber,
            documentType: props.initialValues.header.documentType,
            labels: props.initialValues.header.labels,
            purchasedAt: props.initialValues.header.purchasedAt,
            purchasePlace: props.initialValues.header.purchasePlace,
            linkedDocumentNumber:
              props.initialValues.header.linkedDocumentNumber,
          },
          items: props.initialValues.items.map(({ quantity, ...product }) => ({
            ...product,
            highPrecisionQuantity: product.highPrecisionQuantity || quantity,
          })),
        }
      : initialValues;
  const isFormDataReady =
    (props.editMode && props.initialValues) || !props.editMode;

  return (
    <Formik
      enableReinitialize
      initialStatus={{}}
      validateOnBlur={false}
      validateOnChange={false}
      initialValues={initialFormData}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        const { items, customerData, channelId, header } = values;
        const selectedValues = {
          items,
          header,
          customerData,
          channelId,
        };

        setSubmitting(true);
        setStatus({});
        try {
          props.editMode
            ? await editTransaction(transactionID as string, selectedValues)
            : await addTransaction(values);
          notify(
            message({
              id: props.editMode ? "transactionEdited" : "transactionAdded",
            }),
          );
          navigate(links.transactionsList);
        } catch (exception) {
          setSubmitting(false);
          notify(getErrorMessage(exception));
          const errors = backendErrors(getBackendErrors(exception));
          setStatus(errors);
          focusError();
        }
      }}
      validationSchema={Yup.object().shape({
        header: Yup.object({
          purchasedAt: Yup.string().required(
            message({ id: "thisValueShouldNotBeBlank" }),
          ),
          documentNumber: Yup.string().required(
            message({ id: "thisValueShouldNotBeBlank" }),
          ),
        }),
        items: Yup.array(
          Yup.object({
            name: Yup.string().required(
              message({ id: "thisValueShouldNotBeBlank" }),
            ),
            sku: Yup.string().required(
              message({ id: "thisValueShouldNotBeBlank" }),
            ),
            highPrecisionQuantity: Yup.string().required(
              message({ id: "thisValueShouldNotBeBlank" }),
            ),
            grossValue: Yup.string().required(
              message({ id: "thisValueShouldNotBeBlank" }),
            ),
            category: Yup.string().required(
              message({ id: "thisValueShouldNotBeBlank" }),
            ),
          }),
        ),
      })}
    >
      {({
        handleSubmit,
        isSubmitting,
        status,
        dirty,
        values,
        setFieldValue,
        initialValues,
      }) => {
        const isReturnTransaction = values?.header?.documentType === "return";
        const formError = status?.[""];

        return (
          <Form onSubmit={handleSubmit} className={classes.form}>
            <TransactionDetails
              isFormDataReady={!!isFormDataReady}
              formError={formError}
              initialValues={props.initialValues}
              editMode={props.editMode}
              setFieldValue={setFieldValue}
              isReturnTransaction={isReturnTransaction}
            />
            <MemberDetails
              isFormDataReady={!!isFormDataReady}
              editMode={props.editMode}
            />
            <TransactionProducts
              items={values.items as TransactionItem[]}
              isFormDataReady={!!isFormDataReady}
            />
            <Box mt={12} />
            <FormButtons
              disableSubmit={isSubmitting}
              onCancelClick={() => navigate(links.transactionsList)}
              onSubmitClick={() => handleSubmit()}
              {...(!props.editMode && { submitMessage: "addTransaction" })}
            />
            {!isSubmitting && (
              <LeaveSiteGuard
                warnBeforeRefresh
                isDirty={dirty}
                initialValues={initialValues}
                currentValues={values}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
}
